import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ShieldCheck, Check, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { Field } from '../Shared';

const SUBS = ['Identité', 'Parole', 'Réflexes', 'Singularités', 'Souvenirs', 'Zones rouges'];

export default function AdminPersona() {
  const [persona, setPersona] = useState(null);
  const [sub, setSub] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/api/admin/persona').then(setPersona).catch((e) => setErr(e.message));
  }, []);

  const set = (k) => (e) => {
    setPersona({ ...persona, [k]: e.target.value });
    setDirty(true);
    setSaved(false);
  };

  const editAnecdotes = (anecdotes) => {
    setPersona({ ...persona, anecdotes });
    setDirty(true);
    setSaved(false);
  };
  const addAnecdote = () => editAnecdotes([...persona.anecdotes, { title: '', body: '', confidentiality: 'vert' }]);
  const rmAnecdote = (i) => editAnecdotes(persona.anecdotes.filter((_, idx) => idx !== i));
  const editAnecdote = (i, key, val) => editAnecdotes(persona.anecdotes.map((a, idx) => (idx === i ? { ...a, [key]: val } : a)));

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const data = await api.patch('/api/admin/persona', persona);
      setPersona(data);
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!persona) return <div className="empty">{err || 'Chargement…'}</div>;

  return (
    <>
      <div className="tabs sub">
        {SUBS.map((t, i) => (
          <button key={t} className={`tab${sub === i ? ' active' : ''}`} onClick={() => setSub(i)}>{t}</button>
        ))}
      </div>
      <div className="panel">
        {sub === 0 && (<>
          <Field label="Prénom" value={persona.prenom} onChange={set('prenom')} placeholder="Olivier" />
          <Field label="Parcours (résumé)" value={persona.parcours} onChange={set('parcours')} placeholder="Lyonnais, ingé devenu prof de ski le week-end…" />
          <Field label="Métier (comme il en parle)" value={persona.metier} onChange={set('metier')} placeholder="« Je fais des tableaux Excel pour sauver le monde »" />
          <Field label="Sa moitié" value={persona.moitie} onChange={set('moitie')} placeholder="Marie-Alix" />
          <Field label="Leur histoire (2 lignes)" value={persona.histoire} onChange={set('histoire')} area placeholder="Rencontrés en 2018…" />
          <Field label="Passions" value={persona.passions} onChange={set('passions')} placeholder="café de spécialité, rando, jeux de mots pourris" />
          <Field label="Valeurs" value={persona.valeurs} onChange={set('valeurs')} placeholder="fidélité, déconne, générosité" />
          <Field label="Ce dont il est fier / qui l'a marqué" value={persona.fierte} onChange={set('fierte')} area placeholder="son équipe, son club de foot, son grand-père…" />
        </>)}
        {sub === 1 && (<>
          <Field label="Ton général" value={persona.ton} onChange={set('ton')} placeholder="chaleureux, taquin, autodérision" />
          <Field label="Débit — vite ou lentement, selon quoi ?" value={persona.debit} onChange={set('debit')} placeholder="vite quand il s'emballe, posé quand il conseille" />
          <Field label="Registre — anglicismes, verlan, accent, patois" value={persona.registre} onChange={set('registre')} area placeholder="« deadline », « c'est chanmé », pointe d'accent du Sud-Ouest…" />
          <Field label="Expressions fétiches (verbatim)" value={persona.expressions} onChange={set('expressions')} area placeholder="« Bref. », « Voilà voilà », « c'est pas faux »" />
          <Field label="Jurons et exclamations (y compris au volant)" value={persona.jurons} onChange={set('jurons')} area placeholder="« Purée ! », jamais pire ; au volant c'est une autre histoire" />
          <Field label="Comment il ouvre et clôt un échange" value={persona.ouverture_cloture} onChange={set('ouverture_cloture')} area placeholder="« Alors ?! » pour commencer, « Bon, je te laisse » pour finir" />
        </>)}
        {sub === 2 && (<>
          <div className="rz-note">
            <ShieldCheck size={15} />
            Ces réflexes pèsent plus lourd que la biographie : c'est la façon d'encaisser une
            taquinerie ou d'esquiver une question gênante qui fait reconnaître quelqu'un.
          </div>
          <Field label="Quand il est content, agacé, ému, ou face à une bonne nouvelle" value={persona.reactions_emotions} onChange={set('reactions_emotions')} area placeholder="« Ahhh mais c'est génial ça ! » ; quand il est ému il fait une blague" />
          <Field label="Quand on le taquine ou qu'on le complimente" value={persona.face_taquinerie} onChange={set('face_taquinerie')} area placeholder="renvoie la balle systématiquement ; balaie les compliments d'un revers de main" />
          <Field label="Face à une question gênante" value={persona.face_gene} onChange={set('face_gene')} area placeholder="joue le jeu deux secondes puis détourne avec une vanne" />
          <Field label="En soirée, en groupe" value={persona.en_societe} onChange={set('en_societe')} placeholder="observateur qui place la punchline au bon moment" />
        </>)}

        {sub === 3 && (<>
          <Field label="Type d'humour" value={persona.humour} onChange={set('humour')} placeholder="second degré, blagues nulles assumées" />
          <Field label="3 traits dominants" value={persona.traits} onChange={set('traits')} area placeholder="généreux, tête de mule, gaffeur — avec un exemple pour chacun" />
          <Field label="Opinions tranchées (inoffensives)" value={persona.opinions} onChange={set('opinions')} area placeholder="team chocolatine, l'ananas sur pizza c'est non" />
          <Field label="Autodérision" value={persona.autoderision} onChange={set('autoderision')} area placeholder="son sens de l'orientation catastrophique" />
          <Field label="Manies, rituels, superstitions" value={persona.manies} onChange={set('manies')} area placeholder="range les verres par taille ; café avant toute conversation" />
          <Field label="Ce qu'il fait et que personne d'autre ne fait" value={persona.signature} onChange={set('signature')} area placeholder="sa tenue signature, ce qu'il commande toujours au bar" />
          <Field label="Le sujet dont il ne se lasse jamais" value={persona.sujet_radeau} onChange={set('sujet_radeau')} placeholder="le café de spécialité, sur trois heures s'il le faut" />
          <Field label="Sa contradiction attachante" value={persona.contradiction} onChange={set('contradiction')} area placeholder="ultra organisé au travail, chaos absolu à la maison" />
          <Field label="Ce qu'il dirait à ses amis dans un discours" value={persona.mot_aux_amis} onChange={set('mot_aux_amis')} area placeholder="matière précieuse pour le mode discours" />
        </>)}

        {sub === 4 && (<>
          <div className="anec-head">
            <span>Souvenirs — à la 1re personne, avec la chute. Confidentialité rouge = jamais injecté dans le prompt.</span>
            <button className="mini-btn" onClick={addAnecdote}><Plus size={14} /> Ajouter</button>
          </div>
          {persona.anecdotes.map((a, i) => (
            <div key={i} className="anec">
              <div className="anec-row">
                <input className="anec-title" value={a.title || ''} placeholder="Titre court" onChange={(e) => editAnecdote(i, 'title', e.target.value)} />
                <select className="anec-conf" value={a.confidentiality || 'vert'} onChange={(e) => editAnecdote(i, 'confidentiality', e.target.value)}>
                  <option value="vert">vert</option>
                  <option value="orange">orange</option>
                  <option value="rouge">rouge</option>
                </select>
                <button className="anec-del" onClick={() => rmAnecdote(i)} aria-label="Supprimer"><Trash2 size={15} /></button>
              </div>
              <textarea className="anec-text" value={a.body || ''} placeholder="Le souvenir en 3-5 lignes…" onChange={(e) => editAnecdote(i, 'body', e.target.value)} />
            </div>
          ))}
          {persona.anecdotes.length === 0 && <div className="empty">Aucun souvenir pour l'instant.</div>}
        </>)}
        {sub === 5 && (<>
          <Field label="Sujets totalement interdits" value={persona.zones_rouges} onChange={set('zones_rouges')} area placeholder="santé, argent, ex, disputes, deuils" />
          <div className="rz-note">
            <ShieldCheck size={15} />
            Filtré côté serveur avant et après le modèle : si on l'y pousse, Oliv'IA esquive avec une pirouette drôle
            et change de sujet, sans casser le personnage.
          </div>
        </>)}

        {err && <div className="test err">{err}</div>}
        <div className="save-bar">
          <button className="btn-primary" onClick={save} disabled={!dirty || saving}>
            {saving ? <><Loader2 size={15} className="spin" /> Enregistrement…</> : saved ? <><Check size={15} /> Enregistré</> : 'Enregistrer le persona'}
          </button>
          {dirty && <span className="hint">Modifications non enregistrées — le prompt sera recompilé (v{persona.version}).</span>}
        </div>
      </div>
    </>
  );
}
